/**
 * useSyncStepWithRoute — centralizes step-to-route synchronization.
 *
 * Each step component calls this hook on mount to push the correct `PfStep`
 * value into `stepNavSlice.currentStep`. This prevents individual step
 * components from each hand-rolling `goToStep` calls and keeps the step
 * indicator (future Module 12 breadcrumb) in sync with the active route.
 *
 * Pattern: call once per step page, passing the step name matching the URL
 * slug. The hook fires a single `goToStep` call on mount only.
 *
 * It also announces the entry to analytics, and resets the window scroll. Step navigation is a same-document
 * route change, so the browser keeps the previous offset: a client who scrolled
 * down the catalogue landed mid-page on Instructions, below the first fields
 * they have to fill. PF v2 scrolls the window (its step bodies carry no
 * overflow container of their own), so `window.scrollTo` is the right lever.
 */

import { useCallback, useEffect, useRef } from 'react'

import type { PfStep } from 'models/purchaseFlowRevamp'
import { logger } from 'utils/logging'
import { ScreenEntryLayout, UNMEASURED_SCREEN_ENTRY_LAYOUT, measureScreenEntryLayout } from 'utils/analytics/screenEntryLayout'
import { usePfStore } from '../store'
import { PfRevampEventName } from '../analytics/events'
import type { InvoiceOption } from '../analytics/events'
import { dispatchPfRevampEvent } from '../analytics/usePfRevampAnalytics'
import { announceFlowLoaded } from './flowLoadedGate'

/** How long the billing entry waits for the payment options that decide the invoice card. */
export const ENTRY_OPTIONS_SETTLE_TIMEOUT_MS = 10_000

/** What a step knows about its entry. Billing: the invoice card, undefined until the payment options settled. */
export interface ScreenEntryDetails {
  invoiceOption?: InvoiceOption | null
}

/**
 * The billing and summary entries as legacy sends them: with the measured layout and, on billing, the invoice card.
 * Once per mount whatever happens: unsettled options send the card as unknown after the timeout, leaving first sends
 * what is known, unmeasured.
 */
const useMeasuredScreenEntry = (step: PfStep | null, invoiceOption: InvoiceOption | null | undefined) => {
  const sentRef = useRef(false)
  const mountedRef = useRef(true)
  const knownInvoiceOptionRef = useRef<InvoiceOption | null>(null)

  const send = useCallback((layout: ScreenEntryLayout) => {
    if (!step || sentRef.current) return
    sentRef.current = true
    dispatchPfRevampEvent({
      type: PfRevampEventName.SCREEN_ENTERED,
      payload: {
        step,
        ctaInViewport: layout.cta_in_viewport,
        stepContentOverflows: layout.step_content_overflows,
        ...(step === 'billing' && { invoiceOption: knownInvoiceOptionRef.current }),
      },
    })
  }, [step])

  const measureAndSend = useCallback(() => {
    const page = document.scrollingElement ?? document.documentElement
    void measureScreenEntryLayout(page).then((layout) => { if (mountedRef.current) send(layout) })
  }, [send])

  // Mount-only, and declared before the measuring effect so its cleanup runs first: a measurement that lands
  // after the unmount finds the entry already sent.
  useEffect(() => {
    const timer = step === 'billing'
      ? window.setTimeout(() => {
        if (sentRef.current) return
        logger.warn('Payment options did not settle before the billing entry', undefined, {
          context: { component: 'useSyncStepWithRoute', action: 'billingOptionsUnsettled' },
        })
        measureAndSend()
      }, ENTRY_OPTIONS_SETTLE_TIMEOUT_MS)
      : undefined
    return () => {
      mountedRef.current = false
      window.clearTimeout(timer)
      send(UNMEASURED_SCREEN_ENTRY_LAYOUT)
    }
  }, [step, send, measureAndSend])

  // The cart nav renders the store's step: until it is this one, the button on screen is the previous step's.
  const isNavOnStep = usePfStore((s) => s.currentStep) === step
  const isSettled = isNavOnStep && (step === 'summary' || invoiceOption !== undefined)
  const measurementStartedRef = useRef(false)
  useEffect(() => {
    if (!step || !isSettled || sentRef.current) return
    // Kept current while the measurement runs, so the latest known card is what gets sent.
    knownInvoiceOptionRef.current = invoiceOption ?? null
    if (measurementStartedRef.current) return
    measurementStartedRef.current = true
    measureAndSend()
  }, [step, isSettled, invoiceOption, measureAndSend])
}

/** Steps whose entry carries what the client could see, and is therefore sent once that is measured. */
const MEASURED_ENTRY_STEPS: ReadonlySet<PfStep> = new Set<PfStep>(['billing', 'summary'])

/**
 * Syncs `stepNavSlice.currentStep` with the mounted step route.
 *
 * @param step - The `PfStep` value matching this route (e.g., `'configuration'`).
 * @param entry - What the step knows about its entry; billing passes its invoice card.
 */
export const useSyncStepWithRoute = (step: PfStep, entry: ScreenEntryDetails = {}): void => {
  const goToStep = usePfStore((s) => s.goToStep)
  const isMeasuredEntry = MEASURED_ENTRY_STEPS.has(step)

  useMeasuredScreenEntry(isMeasuredEntry ? step : null, entry.invoiceOption)

  useEffect(() => {
    goToStep(step)
    // Fired on ARRIVAL, not on the navigation that caused it, so the step the
    // flow opens on is counted like any other. That is the funnel's top, and
    // legacy has never had one.
    if (!isMeasuredEntry) {
      dispatchPfRevampEvent({
        type: PfRevampEventName.SCREEN_ENTERED,
        payload: { step },
      })
    }
    // A step can also BE the first screen of the visit — a resumed draft or a
    // bookmark opens the flow straight onto one. The gate ignores this unless
    // it is the first screen to announce itself.
    announceFlowLoaded(step)
    // `behavior: 'auto'` on purpose — a smooth scroll from a deep offset would
    // animate across a step that is already rendering its first fields.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    // `step` is a constant string per call site; `goToStep` is a stable Zustand
    // action reference. Listing both keeps lint happy without semantic change.
  }, [step, goToStep, isMeasuredEntry])
}
